import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProjects = () => {
  const { getAuthHeader } = useAuth();
  const [projects, setProjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    type: '',
    status: '',
    price_range: '',
    area: '',
    bedrooms: '',
    amenities: '',
    image_url: '',
    gallery_images: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        ...project,
        amenities: project.amenities.join(', '),
        gallery_images: project.gallery_images ? project.gallery_images.join(', ') : ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        location: '',
        type: '',
        status: '',
        price_range: '',
        area: '',
        bedrooms: '',
        amenities: '',
        image_url: '',
        gallery_images: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
      gallery_images: formData.gallery_images.split(',').map(url => url.trim()).filter(url => url)
    };

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Project updated successfully!');
      } else {
        await axios.post(`${API}/projects`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Project created successfully!');
      }
      fetchProjects();
      closeModal();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${API}/projects/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Project deleted successfully!');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-playfair font-bold text-stone-900 mb-2">Projects</h1>
          <p className="text-stone-600">Manage your property listings</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="add-project-button"
          className="bg-emerald-900 hover:bg-emerald-800 text-white px-6 py-3 uppercase tracking-widest text-xs font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> ADD PROJECT
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div
            key={project.id}
            data-testid={`project-item-${project.id}`}
            className="bg-white border border-stone-200 overflow-hidden"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={project.image_url}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="text-xl font-playfair font-bold text-stone-900 mb-2">{project.title}</h3>
              <p className="text-stone-600 text-sm mb-2">{project.location}</p>
              <p className="text-stone-700 line-clamp-2 text-sm mb-4">{project.description}</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => openModal(project)}
                  data-testid={`edit-project-${project.id}`}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  onClick={() => handleDelete(project.id)}
                  data-testid={`delete-project-${project.id}`}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500">No projects yet. Add your first project!</p>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="project-form-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-playfair font-bold">
              {editingProject ? 'Edit Project' : 'Add New Project'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Project Details</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details">
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="title"
                  placeholder="Project Title *"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="project-title-input"
                />
                <Textarea
                  name="description"
                  placeholder="Description *"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="project-description-input"
                />
                <Input
                  name="location"
                  placeholder="Location *"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  data-testid="project-location-input"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    name="type"
                    placeholder="Type (e.g., Residential) *"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    data-testid="project-type-input"
                  />
                  <Input
                    name="status"
                    placeholder="Status (e.g., Completed) *"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    data-testid="project-status-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    name="price_range"
                    placeholder="Price Range"
                    value={formData.price_range}
                    onChange={handleChange}
                    data-testid="project-price-input"
                  />
                  <Input
                    name="area"
                    placeholder="Area (e.g., 2000 sq ft)"
                    value={formData.area}
                    onChange={handleChange}
                    data-testid="project-area-input"
                  />
                  <Input
                    name="bedrooms"
                    placeholder="Bedrooms"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    data-testid="project-bedrooms-input"
                  />
                </div>
                <Input
                  name="amenities"
                  placeholder="Amenities (comma separated)"
                  value={formData.amenities}
                  onChange={handleChange}
                  data-testid="project-amenities-input"
                />
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    data-testid="project-submit-button"
                    className="flex-1 bg-emerald-900 hover:bg-emerald-800"
                  >
                    {editingProject ? 'UPDATE PROJECT' : 'CREATE PROJECT'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeModal}
                    data-testid="project-cancel-button"
                  >
                    CANCEL
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="images" className="space-y-4">
              <div>
                <h3 className="text-lg font-bold mb-2">Main Image (Thumbnail)</h3>
                <ImageUploader
                  onImageUploaded={(url) => setFormData({ ...formData, image_url: url })}
                  multiple={false}
                />
                {formData.image_url && (
                  <div className="mt-2">
                    <p className="text-sm text-stone-600 mb-1">Current main image:</p>
                    <img src={formData.image_url} alt="Main" className="w-full h-40 object-cover border" />
                  </div>
                )}
                <p className="text-xs text-stone-500 mt-2">Or paste URL:</p>
                <Input
                  name="image_url"
                  placeholder="Main Image URL"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div>
                <h3 className="text-lg font-bold mb-2">Gallery Images</h3>
                <ImageUploader
                  onImageUploaded={(urls) => {
                    const currentGallery = formData.gallery_images ? formData.gallery_images.split(',').map(u => u.trim()).filter(u => u) : [];
                    const newGallery = [...currentGallery, ...urls].join(', ');
                    setFormData({ ...formData, gallery_images: newGallery });
                  }}
                  multiple={true}
                />
                <p className="text-xs text-stone-500 mt-2">Or paste URLs (comma separated):</p>
                <textarea
                  name="gallery_images"
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                  value={formData.gallery_images}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 resize-none mt-1"
                />
              </div>

              <Button
                onClick={handleSubmit}
                className="w-full bg-emerald-900 hover:bg-emerald-800"
              >
                {editingProject ? 'UPDATE PROJECT' : 'CREATE PROJECT'}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;